import { useState, useEffect } from "react";
import API from "@/lib/api";
import { BarChart3, Download, FileText, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from "recharts";

function formatKES(num) {
  if (num == null) return "KES 0";
  return `KES ${Number(num).toLocaleString("en-KE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatKES2(num) {
  if (num == null) return "0.00";
  return Number(num).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ReportsPage() {
  const { toast } = useToast();
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [deptCosts, setDeptCosts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  // P9
  const [p9Employee, setP9Employee] = useState("");
  const [p9Year, setP9Year] = useState(String(new Date().getFullYear()));
  const [p9Data, setP9Data] = useState(null);
  // P10
  const [p10Month, setP10Month] = useState("");
  const [p10Data, setP10Data] = useState(null);
  // Payroll Deductions Report
  const [dedMonth, setDedMonth] = useState("");
  const [dedData, setDedData] = useState(null);
  const [dedLoading, setDedLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/reports/payroll-summary").then(({ data }) => { setPayrollSummary(data); setRuns(data); }),
      API.get("/reports/department-costs").then(({ data }) => setDeptCosts(data)),
      API.get("/employees").then(({ data }) => setEmployees(data)),
    ]).catch(console.error).finally(() => setLoading(false));
  }, []);

  const trendData = [...payrollSummary].reverse().map((r) => ({
    month: r.month, gross: r.total_gross, net: r.total_net, deductions: r.total_deductions,
  }));

  const fetchP9 = async () => {
    if (!p9Employee) return;
    try {
      const { data } = await API.get(`/reports/p9-data/${p9Employee}?year=${p9Year}`);
      setP9Data(data);
    } catch { setP9Data(null); }
  };

  const downloadP9 = async () => {
    if (!p9Employee) return;
    try {
      const response = await API.get(`/reports/p9/${p9Employee}?year=${p9Year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `P9_${p9Year}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to download P9";
      toast({ variant: "destructive", title: "Download Failed", description: msg });
    }
  };

  const fetchP10 = async () => {
    if (!p10Month) return;
    try {
      const { data } = await API.get(`/reports/p10-data?month=${p10Month}`);
      setP10Data(data);
    } catch { setP10Data(null); }
  };

  const downloadP10 = async () => {
    if (!p10Month) return;
    try {
      const response = await API.get(`/reports/p10?month=${p10Month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `P10_${p10Month}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to download P10";
      toast({ variant: "destructive", title: "Download Failed", description: msg });
    }
  };

  // Payroll Deductions Report handlers
  const fetchDeductions = async () => {
    if (!dedMonth) return;
    setDedLoading(true);
    try {
      const { data } = await API.get(`/reports/payroll-deductions?month=${dedMonth}`);
      setDedData(data);
    } catch (err) {
      setDedData(null);
      const msg = err.response?.data?.detail || err.message || "Failed to load deductions data";
      toast({ variant: "destructive", title: "Error", description: msg });
    }
    finally { setDedLoading(false); }
  };

  const downloadDedExcel = async () => {
    if (!dedMonth) return;
    try {
      const response = await API.get(`/reports/payroll-deductions/excel?month=${dedMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Payroll_Deductions_${dedMonth}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to download Excel report";
      toast({ variant: "destructive", title: "Download Failed", description: msg });
    }
  };

  const downloadDedPdf = async () => {
    if (!dedMonth) return;
    try {
      const response = await API.get(`/reports/payroll-deductions/pdf?month=${dedMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Payroll_Deductions_${dedMonth}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to download PDF report";
      toast({ variant: "destructive", title: "Download Failed", description: msg });
    }
  };

  const availableMonths = runs.map((r) => r.month);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
        Reports
      </h1>

      <Tabs defaultValue="payroll" className="w-full">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="payroll" data-testid="tab-payroll-report">Payroll Summary</TabsTrigger>
          <TabsTrigger value="department" data-testid="tab-department-report">Department Costs</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends-report">Trends</TabsTrigger>
          <TabsTrigger value="deductions" data-testid="tab-deductions-report">Deduction Report</TabsTrigger>
          <TabsTrigger value="p9" data-testid="tab-p9-report">P9 Tax Card</TabsTrigger>
          <TabsTrigger value="p10" data-testid="tab-p10-report">P10 PAYE Return</TabsTrigger>
        </TabsList>

        {/* Payroll Summary */}
        <TabsContent value="payroll">
          {loading ? <div className="text-center py-12 text-sm text-slate-500">Loading...</div> : payrollSummary.length === 0 ? (
            <div className="text-center py-12 border border-slate-200 rounded-sm"><BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No payroll data yet</p></div>
          ) : (
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Month</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Employees</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Total Gross</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Total Deductions</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Total Net</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payrollSummary.map((r) => (
                    <TableRow key={r.month} className="border-b border-slate-100 hover:bg-slate-50">
                      <TableCell className="px-4 py-3 font-medium text-sm text-slate-900">{r.month}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 text-right">{r.employee_count}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-900 text-right font-mono">{formatKES(r.total_gross)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-[#991B1B] text-right font-mono">{formatKES(r.total_deductions)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-[#065F46] text-right font-mono font-medium">{formatKES(r.total_net)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Department Costs */}
        <TabsContent value="department">
          {deptCosts.length === 0 ? (
            <div className="text-center py-12 border border-slate-200 rounded-sm"><BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No data yet</p></div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-6">Department-wise Payroll Costs</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={deptCosts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#4B5563" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatKES(v)} />
                    <Bar dataKey="total_gross" fill="#002FA7" name="Gross" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="total_net" fill="#94A3B8" name="Net" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends">
          {trendData.length === 0 ? (
            <div className="text-center py-12 border border-slate-200 rounded-sm"><BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No trend data yet</p></div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-6">Payroll Trends</h3>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => formatKES(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="gross" stroke="#002FA7" name="Gross" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="net" stroke="#065F46" name="Net" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="deductions" stroke="#991B1B" name="Deductions" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        {/* Payroll Deductions Report */}
        <TabsContent value="deductions">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><FileSpreadsheet className="h-5 w-5 text-[#002FA7]" /></div>
                <div><h3 className="font-medium text-slate-900">Payroll Deductions Report</h3><p className="text-xs text-slate-500">Employee-wise deductions breakdown with totals for accounting</p></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Month</label>
                  <Select value={dedMonth} onValueChange={setDedMonth}>
                    <SelectTrigger data-testid="ded-month-select" className="h-9"><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>{availableMonths.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <button data-testid="ded-view-btn" onClick={fetchDeductions} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-4 py-2 text-sm font-medium">
                    {dedLoading ? "Loading..." : "View"}
                  </button>
                  <button data-testid="ded-excel-btn" onClick={downloadDedExcel} disabled={!dedData} className="bg-[#1D6F42] hover:bg-[#155835] text-white rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-1 disabled:opacity-50">
                    <Download className="h-3.5 w-3.5" />Excel
                  </button>
                  <button data-testid="ded-pdf-btn" onClick={downloadDedPdf} disabled={!dedData} className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-1 disabled:opacity-50">
                    <Download className="h-3.5 w-3.5" />PDF
                  </button>
                </div>
              </div>
            </div>
            {dedData && (
              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="ded-result">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900">{dedData.company_name} — {dedData.month}</h4>
                    <p className="text-xs text-slate-500">{dedData.employer_location} | {dedData.organization_email}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Employees</div>
                    <div className="text-lg font-medium text-slate-900">{dedData.employee_count}</div>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      {["#", "Employee", "KRA PIN", "Dept", "Gross Salary", "PAYE", "SHA/SHIF", "NSSF T1", "NSSF T2", "NSSF Total", "Housing Levy", "HELB", "Advance", "Other Pens.", "Total Ded.", "Net Salary"].map((h) => (
                        <TableHead key={h} className="px-2 py-2 text-xs uppercase tracking-wider text-slate-500 font-medium text-right whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {dedData.rows.map((r, i) => (
                        <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <TableCell className="px-2 py-2 text-sm text-slate-500 text-right">{r.sn}</TableCell>
                          <TableCell className="px-2 py-2 text-sm font-medium text-slate-900 text-left whitespace-nowrap">{r.employee_name}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-slate-700 text-left">{r.kra_pin || "—"}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-slate-700 text-left">{r.department || "—"}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-slate-900 text-right font-mono">{formatKES2(r.gross_pay)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.paye)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.shif)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.nssf_tier1)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.nssf_tier2)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.nssf_total)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.housing_levy)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.helb)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.advance_deduction)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES2(r.other_pension)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-medium">{formatKES2(r.total_deductions)}</TableCell>
                          <TableCell className="px-2 py-2 text-sm text-[#065F46] text-right font-mono font-medium">{formatKES2(r.net_pay)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow className="bg-slate-100 font-semibold">
                        <TableCell className="px-2 py-2 text-sm text-slate-900 text-right"></TableCell>
                        <TableCell className="px-2 py-2 text-sm font-bold text-slate-900 text-left">TOTALS</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-slate-900 text-left"></TableCell>
                        <TableCell className="px-2 py-2 text-sm text-slate-900 text-left"></TableCell>
                        <TableCell className="px-2 py-2 text-sm text-slate-900 text-right font-mono font-bold">{formatKES2(dedData.totals.gross_pay)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.paye)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.shif)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.nssf_tier1)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.nssf_tier2)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.nssf_total)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.housing_levy)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.helb)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.advance_deduction)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.other_pension)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#991B1B] text-right font-mono font-bold">{formatKES2(dedData.totals.total_deductions)}</TableCell>
                        <TableCell className="px-2 py-2 text-sm text-[#065F46] text-right font-mono font-bold">{formatKES2(dedData.totals.net_pay)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* P9 Tax Deduction Card */}

        <TabsContent value="p9">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><FileText className="h-5 w-5 text-[#002FA7]" /></div>
                <div><h3 className="font-medium text-slate-900">P9 Tax Deduction Card</h3><p className="text-xs text-slate-500">Annual employee tax deduction summary for KRA</p></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Employee</label>
                  <Select value={p9Employee} onValueChange={setP9Employee}>
                    <SelectTrigger data-testid="p9-employee-select" className="h-9"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Year</label>
                  <input data-testid="p9-year" type="number" value={p9Year} onChange={(e) => setP9Year(e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm" />
                </div>
                <div className="flex items-end gap-2">
                  <button data-testid="p9-view-btn" onClick={fetchP9} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-4 py-2 text-sm font-medium">View</button>
                  <button data-testid="p9-download-btn" onClick={downloadP9} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-1"><Download className="h-3.5 w-3.5" />PDF</button>
                </div>
              </div>
            </div>
            {p9Data && (
              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="p9-result">
                <h4 className="text-sm font-medium text-slate-900 mb-1">{p9Data.employee_name} — {p9Data.year}</h4>
                <p className="text-xs text-slate-500 mb-4">KRA PIN: {p9Data.kra_pin || "Not set"}</p>
                {p9Data.monthly?.length === 0 ? <p className="text-sm text-slate-500">No payslip data for this year.</p> : (
                  <div className="border border-slate-200 rounded-sm overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50">
                        {["Month", "Gross Pay", "NSSF", "SHIF", "Housing Levy", "Taxable Pay", "PAYE", "Net Pay"].map((h) => (
                          <TableHead key={h} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-medium text-right whitespace-nowrap">{h}</TableHead>
                        ))}
                      </TableRow></TableHeader>
                      <TableBody>
                        {p9Data.monthly.map((m) => (
                          <TableRow key={m.month} className="border-b border-slate-100 hover:bg-slate-50">
                            <TableCell className="px-3 py-2 text-sm font-medium text-slate-900 text-right">{m.month}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-slate-700 text-right font-mono">{formatKES(m.gross_pay)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(m.nssf)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(m.shif)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(m.housing_levy)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-slate-700 text-right font-mono">{formatKES(m.taxable_income)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(m.paye)}</TableCell>
                            <TableCell className="px-3 py-2 text-sm text-[#065F46] text-right font-mono font-medium">{formatKES(m.net_pay)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* P10 Monthly PAYE Return */}
        <TabsContent value="p10">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><FileText className="h-5 w-5 text-[#002FA7]" /></div>
                <div><h3 className="font-medium text-slate-900">P10 Monthly PAYE Return</h3><p className="text-xs text-slate-500">Employer monthly PAYE return for KRA submission</p></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Month</label>
                  <Select value={p10Month} onValueChange={setP10Month}>
                    <SelectTrigger data-testid="p10-month-select" className="h-9"><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>{availableMonths.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <button data-testid="p10-view-btn" onClick={fetchP10} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-4 py-2 text-sm font-medium">View</button>
                  <button data-testid="p10-download-btn" onClick={downloadP10} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-1"><Download className="h-3.5 w-3.5" />PDF</button>
                </div>
              </div>
            </div>
            {p10Data && (
              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="p10-result">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-slate-900">P10 — {p10Data.month}</h4>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Total PAYE</div>
                    <div className="text-lg font-medium text-[#991B1B] font-mono">{formatKES(p10Data.total_paye)}</div>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      {["#", "Employee", "KRA PIN", "Gross Pay", "NSSF", "SHIF", "Housing", "Taxable", "PAYE", "Net Pay"].map((h) => (
                        <TableHead key={h} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-medium text-right whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {p10Data.employees?.map((e, i) => (
                        <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <TableCell className="px-3 py-2 text-sm text-slate-500 text-right">{i + 1}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-medium text-slate-900 text-left">{e.name}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-slate-700 text-left">{e.kra_pin || "—"}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-slate-700 text-right font-mono">{formatKES(e.gross_pay)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(e.nssf)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(e.shif)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono">{formatKES(e.housing_levy)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-slate-700 text-right font-mono">{formatKES(e.taxable_income)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-[#991B1B] text-right font-mono font-medium">{formatKES(e.paye)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-[#065F46] text-right font-mono">{formatKES(e.net_pay)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
